import React, { memo, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translateStoreCategory } from "../../../utils/storeUtils";
import ShinyText from "./ShinyText";
import styles from "./OurCategories.module.css";

// Intentionally no default categories.
// Admin-defined categories should be the single source of truth.
const DEFAULT_CATEGORIES = [];

function slugFromCategory(cat) {
  if (cat?.slug && typeof cat.slug === "string") {
    return cat.slug.toLowerCase().trim().replace(/[\s-]+/g, "");
  }
  return String(cat?.label || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s-]+/g, "");
}

/** Left column: sofas (full height) + tables + decor | chairs */
const LEFT_SLOTS = [
  { id: "sofa", slugs: ["sofas", "sofa"], tileClass: styles.tileLeftSofa, labelClass: styles.labelBl },
  {
    id: "tables",
    slugs: ["tables", "table"],
    tileClass: styles.tileLeftTables,
    labelClass: styles.labelTables,
    lightTile: true,
  },
  { id: "decor", slugs: ["decor"], tileClass: styles.tileLeftBottom, labelClass: styles.labelBl },
  { id: "chairs", slugs: ["chairs", "chair"], tileClass: styles.tileLeftBottom, labelClass: styles.labelBl },
];

/** Right column: six tiles in a 2-column grid */
const RIGHT_SLOTS = [
  { slugs: ["beds", "bed"], tileClass: styles.tileRightBeds, labelClass: styles.labelBl },
  { slugs: ["storage"], tileClass: styles.tileRightStorage, labelClass: styles.labelBr },
  { slugs: ["armchairs", "armchair"], tileClass: styles.tileRightArmchairs, labelClass: styles.labelBl },
  { slugs: ["lighting"], tileClass: styles.tileRightLighting, labelClass: styles.labelBl },
  { slugs: ["firerateddoors"], tileClass: styles.tileRightFire, labelClass: styles.labelBr },
  // Row 3 should render: Fire-rated (left) | Doors (right)
  { slugs: ["doors", "door"], tileClass: styles.tileRightDoors, labelClass: styles.labelBr },
];

function pickCategoryForSlot(list, used, slugs) {
  const slugify = (c) => slugFromCategory(c);
  for (const s of slugs) {
    const found = list.find(
      (c) =>
        !used.has(c.label) && String(c.slug || "").toLowerCase() !== "all" && slugify(c) === s
    );
    if (found) return found;
  }
  for (const s of slugs) {
    const found = list.find(
      (c) =>
        !used.has(c.label) && String(c.slug || "").toLowerCase() !== "all" && slugify(c).includes(s)
    );
    if (found) return found;
  }
  return (
    list.find((c) => !used.has(c.label) && String(c.slug || "").toLowerCase() !== "all") || null
  );
}

function getImageUrl(cat, base) {
  if (!cat.image || typeof cat.image !== "string") return "";
  if (cat.image.startsWith("http") || cat.image.startsWith("/")) return cat.image;
  const trimmed = String(base || "").replace(/\/?$/, "");
  return trimmed ? `${trimmed}/${cat.image}` : cat.image;
}

const GridTile = memo(function GridTile({
  category,
  imageUrl,
  tileClass,
  labelClass,
  lightTile,
  isSelected,
  onSelect,
  isRtl,
}) {
  const { t } = useTranslation("global");
  const displayLabel = translateStoreCategory(t, category);
  const handleClick = useCallback(() => {
    onSelect?.(category.label);
  }, [category.label, onSelect]);

  return (
    <li className={`${styles.tile} ${tileClass || ""} ${lightTile ? styles.tileLight : ""}`}>
      <button
        type="button"
        className={`${styles.tileInner} ${isSelected ? styles.tileSelected : ""}`}
        onClick={handleClick}
        aria-label={t("store.ourCategoriesBrowseAria", {
          label: displayLabel,
          defaultValue: "Browse {{label}}",
        })}
        aria-pressed={isSelected}
      >
        {imageUrl ? (
          <img
            className={styles.tileImage}
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        ) : null}
        <div className={styles.tileScrim} aria-hidden="true" />
        {labelClass && (
          <span
            className={`${styles.tileLabel} ${labelClass}`}
            dir={isRtl ? "rtl" : "ltr"}
            lang={isRtl ? "ar" : "en"}
          >
            {displayLabel}
          </span>
        )}
      </button>
    </li>
  );
});

export default function OurCategories({
  categories,
  photosBasePath,
  selectedCategory,
  onCategoryClick,
  variant = "minimal",
}) {
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const list = Array.isArray(categories) ? categories : [];
  const hasRealCategories = list.length > 0;
  const base = photosBasePath || "";
  const sectionVariantClass =
    variant === "bold"
      ? styles.variantBold
      : variant === "luxury"
      ? styles.variantLuxury
      : styles.variantMinimal;

  const bentoList = useMemo(
    () => list.filter((c) => {
      const s = String(c.slug || "").toLowerCase();
      return s !== "all";
    }),
    [list]
  );

  /** Full swipe rail on mobile: API categories plus any canonical slots missing from API (e.g. dev DB with few rows). */
  const mobileCatalogList = useMemo(() => {
    const fromProps = list.filter((c) => {
      const s = String(c.slug || "").toLowerCase();
      return s !== "all";
    });
    const seen = new Set(fromProps.map((c) => slugFromCategory(c)));
    const merged = [...fromProps];
    for (const def of DEFAULT_CATEGORIES) {
      const key = slugFromCategory({ label: def.label });
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push({
        label: def.label,
        slug: key,
        image: def.image,
      });
    }
    const defaultOrder = new Map(
      DEFAULT_CATEGORIES.map((d, i) => [slugFromCategory({ label: d.label }), i])
    );
    merged.sort((a, b) => {
      const ia = defaultOrder.get(slugFromCategory(a));
      const ib = defaultOrder.get(slugFromCategory(b));
      if (ia != null && ib != null) return ia - ib;
      if (ia != null) return -1;
      if (ib != null) return 1;
      return 0;
    });
    return merged;
  }, [list]);

  const { leftById, rightSlots } = useMemo(() => {
    const used = new Set();
    const leftById = {};
    for (const def of LEFT_SLOTS) {
      const category = pickCategoryForSlot(bentoList, used, def.slugs);
      if (!category) continue;
      used.add(category.label);
      leftById[def.id] = { category, ...def };
    }
    const right = RIGHT_SLOTS.map((def) => {
      const category = pickCategoryForSlot(bentoList, used, def.slugs);
      if (!category) return null;
      used.add(category.label);
      return { category, ...def };
    }).filter(Boolean);
    return { leftById, rightSlots: right };
  }, [bentoList]);

  const leftSofa = leftById.sofa
    ? { ...leftById.sofa, imageUrl: getImageUrl(leftById.sofa.category, base) }
    : null;
  const leftTables = leftById.tables
    ? { ...leftById.tables, imageUrl: getImageUrl(leftById.tables.category, base) }
    : null;
  const leftBottom = ["decor", "chairs"]
    .map((id) => {
      const s = leftById[id];
      return s ? { ...s, imageUrl: getImageUrl(s.category, base) } : null;
    })
    .filter(Boolean);

  const rightWithUrls = useMemo(
    () =>
      rightSlots.map((entry) => ({
        ...entry,
        imageUrl: getImageUrl(entry.category, base),
      })),
    [rightSlots, base]
  );

  const rightRow1 = rightWithUrls.slice(0, 2);
  const rightRow2 = rightWithUrls.slice(2, 4);
  const rightRow3 = rightWithUrls.slice(4, 6);

  const renderRightTiles = (slots) =>
    slots.map((slot, i) => (
      <GridTile
        key={slot.category.slug || `${slot.category.label}-${i}`}
        category={slot.category}
        imageUrl={slot.imageUrl}
        tileClass={slot.tileClass}
        labelClass={slot.labelClass}
        lightTile={slot.lightTile}
        isSelected={selectedCategory === slot.category.label}
        onSelect={onCategoryClick}
        isRtl={isRtl}
      />
    ));

  const titleLead = t("store.ourCategoriesTitleLead", { defaultValue: "Our" });
  const titleAccent = t("store.ourCategoriesTitleAccent", {
    defaultValue: "Categories",
  });

  return (
    <section
      className={`store-our-categories ${styles.section} ${sectionVariantClass}`}
      aria-labelledby="store-categories-heading"
      dir={isRtl ? "rtl" : "ltr"}
      lang={isRtl ? "ar" : "en"}
    >
      <div className={styles.shell}>
        <header
          className={styles.headerCell}
          dir={isRtl ? "rtl" : "ltr"}
          lang={isRtl ? "ar" : "en"}
        >
          <h2 id="store-categories-heading" className="store-weekly-title">
            {titleLead ? (
              <>
                <span className="store-weekly-title-lead">{titleLead}</span>{" "}
              </>
            ) : null}
            <span
              className="store-weekly-title-accent"
              dir={isRtl ? "rtl" : "ltr"}
              lang={isRtl ? "ar" : "en"}
            >
              <ShinyText
                text={titleAccent}
                speed={2}
                delay={0}
                color="#6f6458"
                shineColor="#ffffff"
                spread={120}
                direction={isRtl ? "right" : "left"}
                yoyo={false}
                pauseOnHover={false}
                disabled={false}
              />
            </span>
          </h2>
          <div className={styles.headerLine}>
            <p className={styles.subtitle}>
              {t("store.ourCategoriesSubtitle", {
                defaultValue:
                  "Explore our curated collections of exceptional furniture and home decor.",
              })}
            </p>
            {hasRealCategories ? (
              <Link className={styles.viewAll} to="/store/category/all">
                {t("store.ourCategoriesViewAll", { defaultValue: "View all" })}
              </Link>
            ) : null}
          </div>
        </header>

        {!hasRealCategories ? (
          <p className={styles.subtitle} style={{ marginTop: 10 }}>
            {t("store.ourCategoriesEmpty", {
              defaultValue: "Categories will appear here once they are added by the admin.",
            })}
          </p>
        ) : (
          <div
            className={styles.mobileCarousel}
            aria-label={t("store.ourCategoriesTitleAccent", { defaultValue: "Categories" })}
          >
            <ul className={styles.mobileCarouselTrack}>
              {mobileCatalogList.map((cat, i) => (
                <GridTile
                  key={cat.slug || slugFromCategory(cat) || `${cat.label}-${i}`}
                  category={cat}
                  imageUrl={getImageUrl(cat, base)}
                  tileClass={`${styles.mobileCarouselCard} ${styles.mobileCarouselTile}`}
                  labelClass={styles.labelBl}
                  lightTile={false}
                  isSelected={selectedCategory === cat.label}
                  onSelect={onCategoryClick}
                  isRtl={isRtl}
                />
              ))}
            </ul>
            <p className={styles.mobileCarouselHint}>
              {t("store.ourCategoriesSwipeHint", {
                defaultValue: "Swipe sideways to see more categories",
              })}
            </p>
          </div>
        )}

        {hasRealCategories ? <div className={styles.leftCluster}>
          {leftSofa ? (
            <ul className={styles.leftSofaList}>
              <GridTile
                key={leftSofa.category.slug || leftSofa.category.label}
                category={leftSofa.category}
                imageUrl={leftSofa.imageUrl}
                tileClass={leftSofa.tileClass}
                labelClass={leftSofa.labelClass}
                lightTile={leftSofa.lightTile}
                isSelected={selectedCategory === leftSofa.category.label}
                onSelect={onCategoryClick}
                isRtl={isRtl}
              />
            </ul>
          ) : null}
          <div className={styles.leftStack}>
            {leftTables ? (
              <ul className={styles.leftTablesList}>
                <GridTile
                  key={leftTables.category.slug || leftTables.category.label}
                  category={leftTables.category}
                  imageUrl={leftTables.imageUrl}
                  tileClass={leftTables.tileClass}
                  labelClass={leftTables.labelClass}
                  lightTile={leftTables.lightTile}
                  isSelected={selectedCategory === leftTables.category.label}
                  onSelect={onCategoryClick}
                  isRtl={isRtl}
                />
              </ul>
            ) : null}
            {leftBottom.length > 0 ? (
              <ul className={styles.leftBottomList}>
                {leftBottom.map((slot, i) => (
                  <GridTile
                    key={slot.category.slug || `${slot.category.label}-${i}`}
                    category={slot.category}
                    imageUrl={slot.imageUrl}
                    tileClass={slot.tileClass}
                    labelClass={slot.labelClass}
                    lightTile={slot.lightTile}
                    isSelected={selectedCategory === slot.category.label}
                    onSelect={onCategoryClick}
                    isRtl={isRtl}
                  />
                ))}
              </ul>
            ) : null}
          </div>
        </div> : null}

        {hasRealCategories ? <div className={styles.rightCluster}>
          {rightRow1.length > 0 ? (
            <ul className={`${styles.rightRow} ${styles.rightRow5050}`}>{renderRightTiles(rightRow1)}</ul>
          ) : null}
          {rightRow2.length > 0 ? (
            <ul className={`${styles.rightRow} ${styles.rightRow7030}`}>{renderRightTiles(rightRow2)}</ul>
          ) : null}
          {rightRow3.length > 0 ? (
            <ul className={`${styles.rightRow} ${styles.rightRow6040}`}>{renderRightTiles(rightRow3)}</ul>
          ) : null}
        </div> : null}
      </div>
    </section>
  );
}
